'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import clsx from 'clsx';
import debounce from 'lodash-es/debounce';

interface InputProps {
    name: string;
    className?: string;
    defaultValue?: string;
    placeholder?: string;
    onChange: (value: string) => void;
    onEnter?: (value: string) => void;
    disabled?: boolean;
}

export const SearchInput = (props: InputProps) => {
    const {
        name,
        className,
        placeholder,
        onChange = () => {},
        onEnter,
        defaultValue = '',
    } = props;
    const [inputValue, setInputValue] = useState(defaultValue);

    const inputRef = useRef<HTMLInputElement>(null);

    const isCompositingRef = useRef(false);

    const debouncedOnChange = useRef(debounce(onChange, 300)).current;

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && onEnter && !isCompositingRef.current) {
            onEnter(inputValue);
        }
    };

    const handleOnChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement> | CompositionEvent) => {
            const { value } = event.target as HTMLInputElement;

            setInputValue(value);

            if (isCompositingRef.current || !debouncedOnChange) {
                return;
            }

            debouncedOnChange(value);
        },
        [debouncedOnChange]
    );

    useEffect(() => {
        setInputValue(
            defaultValue === null || defaultValue === undefined ? '' : defaultValue
        );
    }, [defaultValue]);

    useEffect(() => {
        const input = inputRef.current;

        function handleComposition(event: CompositionEvent) {
            if (event.type === 'compositionstart') {
                isCompositingRef.current = true;
            }

            if (event.type === 'compositionend') {
                isCompositingRef.current = false;

                handleOnChange(event);
            }
        }

        function handleBlur() {
            if (input) {
                input.classList.remove('is-active');
            }
        }

        if (input) {
            input.addEventListener('compositionstart', handleComposition);
            input.addEventListener('compositionend', handleComposition);
            input.addEventListener('blur', handleBlur);

            return () => {
                input.removeEventListener('compositionstart', handleComposition);
                input.removeEventListener('compositionend', handleComposition);
                input.removeEventListener('blur', handleBlur);
            };
        }

        return () => {};
    }, [handleOnChange]);

    return (
        <div className={clsx('search-bar-box', className)}>
            <input
                id={name}
                className="search-bar-input"
                ref={inputRef}
                value={inputValue}
                onChange={handleOnChange}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
            />
        </div>
    );
};
